// RPKI data processing for Cloudflare's RPKI JSON feed
// Based on RFC 8416 - RPKI Repository Data Structure

export interface ROAEntry {
  prefix: string;      // IP prefix (e.g., "1.0.0.0/24")
  asn: number;        // ASN as number (e.g., 13335) 
  maxLength: number;  // Maximum prefix length allowed
  ta: string;         // Trust Anchor
  expires?: number;   // Expiration timestamp (optional)
}

export interface RPKIData {
  roas: ROAEntry[];
  metadata: {
    buildTime: string;
    serial: number;
    total: number;
  };
}

export interface ProcessedROAData {
  [prefix: string]: {
    asn: string;
    maxLength: number;
    hasROA: boolean;
    ta: string;
    // Enhanced hierarchy fields from Go processing
    hasROAChildren?: boolean;
    roaChildrenCount?: number;
    parentHasROA?: boolean;
  };
}

class RPKIService {
  private roaData: ProcessedROAData = {};
  private loading = false;
  private lastFetch: number = 0;
  private cacheDuration = 10 * 60 * 1000; // 10 minutes
  
  // Optimization: Pre-built lookup structures
  private prefixesByMask: Map<number, string[]> = new Map();
  private dynamicCache: Map<string, any> = new Map();

  async fetchRPKIData(forceReload: boolean = false): Promise<ProcessedROAData> {
    const now = Date.now();
    
    // Return cached data if still fresh and not forcing reload
    if (!forceReload && Object.keys(this.roaData).length > 0 && (now - this.lastFetch) < this.cacheDuration) {
      return this.roaData;
    }

    if (this.loading) {
      // Wait for existing fetch to complete
      while (this.loading) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.roaData;
    }

    this.loading = true;
    
    try {
      console.log('Loading RPKI data from local file...');
      console.log('File URL: /rpki-enhanced.json');
      
      // Fetch from the enhanced file in public directory with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch('/rpki-enhanced.json', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log(`Response status: ${response.status}`);
      console.log(`Response headers:`, response.headers.get('content-type'), response.headers.get('content-length'));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Enhanced RPKI data file not found. Please run: go run process_rpki.go to generate public/rpki-enhanced.json');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Starting JSON parsing...');
      const data: any = await response.json();
      console.log(`Successfully parsed JSON.`);
      
      // Check if this is enhanced format or original format
      if (data.roas && typeof data.roas === 'object' && !Array.isArray(data.roas)) {
        // Enhanced format: data.roas is already a processed object
        console.log(`Found enhanced RPKI data with ${Object.keys(data.roas).length} ROA entries`);
        this.roaData = data.roas;
      } else if (data.roas && Array.isArray(data.roas)) {
        // Original format: data.roas is an array
        console.log(`Found original RPKI data with ${data.roas.length} ROA entries`);
        if (data.roas.length === 0) {
          throw new Error('No ROAs found in data file');
        }
        // Process the ROA data into a lookup table
        console.log('Processing ROA data...');
        this.roaData = this.processROAData(data.roas);
      } else {
        throw new Error('Invalid RPKI data format');
      }
      this.lastFetch = now;
      
      // Build optimization structures after loading data
      this.buildLookupStructures();
      
      console.log(`Successfully processed ${Object.keys(this.roaData).length} ROA entries`);
      return this.roaData;
      
    } catch (error) {
      console.error('Failed to load RPKI data:', error);
      
      // If local file is not available, create demo ROA data for testing
      console.log('File not found, creating demo ROA data for testing...');
      console.log('To use real data: curl https://rpki.cloudflare.com/rpki.json > public/rpki-data.json');
      this.roaData = this.createDemoROAData();
      this.lastFetch = now;
      
      // Build optimization structures for demo data too
      this.buildLookupStructures();
      
      return this.roaData;
    } finally {
      this.loading = false;
    }
  }

  private processROAData(roas: ROAEntry[]): ProcessedROAData {
    const processed: ProcessedROAData = {};
    
    for (const roa of roas) {
      // ASN is a number in the actual data, convert to string with AS prefix
      const cleanASN = `AS${roa.asn}`;
      
      processed[roa.prefix] = {
        asn: cleanASN,
        maxLength: roa.maxLength,
        hasROA: true,
        ta: roa.ta || 'unknown'
      };
    }
    
    return processed;
  }

  // Create demo ROA data if fetching fails
  private createDemoROAData(): ProcessedROAData {
    const demoROAs: ProcessedROAData = {
      // Cloudflare prefixes
      "1.1.1.0/24": { asn: "AS13335", maxLength: 24, hasROA: true, ta: "arin" },
      "1.0.0.0/24": { asn: "AS13335", maxLength: 24, hasROA: true, ta: "arin" },
      "104.16.0.0/16": { asn: "AS13335", maxLength: 24, hasROA: true, ta: "arin" },
      "104.17.0.0/16": { asn: "AS13335", maxLength: 24, hasROA: true, ta: "arin" },
      "104.18.0.0/16": { asn: "AS13335", maxLength: 24, hasROA: true, ta: "arin" },
      
      // Google prefixes
      "8.8.8.0/24": { asn: "AS15169", maxLength: 24, hasROA: true, ta: "arin" },
      "8.8.4.0/24": { asn: "AS15169", maxLength: 24, hasROA: true, ta: "arin" },
      "74.125.0.0/16": { asn: "AS15169", maxLength: 24, hasROA: true, ta: "arin" },
      
      // Other major providers
      "31.13.24.0/24": { asn: "AS32934", maxLength: 24, hasROA: true, ta: "arin" }, // Facebook
      "13.107.42.0/24": { asn: "AS8075", maxLength: 24, hasROA: true, ta: "arin" }, // Microsoft
      "185.199.108.0/22": { asn: "AS36459", maxLength: 24, hasROA: true, ta: "arin" }, // GitHub
    };
    
    console.log(`Created ${Object.keys(demoROAs).length} demo ROA entries for testing`);
    return demoROAs;
  }

  // Check if a specific prefix has ROA coverage
  hasROA(prefix: string): boolean {
    return prefix in this.roaData;
  }

  // Build lookup structures for fast hierarchy calculations
  private buildLookupStructures() {
    console.log('Building optimization structures...');
    this.prefixesByMask.clear();
    this.dynamicCache.clear();
    
    // Group prefixes by mask length for efficient lookup
    for (const prefix of Object.keys(this.roaData)) {
      try {
        const [, maskStr] = prefix.split('/');
        const mask = parseInt(maskStr);
        if (!isNaN(mask)) {
          if (!this.prefixesByMask.has(mask)) {
            this.prefixesByMask.set(mask, []);
          }
          this.prefixesByMask.get(mask)!.push(prefix);
        }
      } catch {
        // Skip invalid prefixes
      }
    }
    
    console.log(`Built lookup structures for ${this.prefixesByMask.size} mask lengths`);
  }

  // Get ROA information for a prefix with dynamic hierarchy calculation
  getROAInfo(prefix: string) {
    // First check if we have direct ROA data
    const directROA = this.roaData[prefix];
    if (directROA) {
      return directROA;
    }

    // Check cache for dynamic calculations
    if (this.dynamicCache.has(prefix)) {
      return this.dynamicCache.get(prefix);
    }

    // If no direct ROA, compute dynamic hierarchy for intermediate prefixes
    const result = this.computeDynamicHierarchy(prefix);
    
    // Cache the result
    if (result) {
      this.dynamicCache.set(prefix, result);
    }
    
    return result;
  }

  // Optimized hierarchy computation using pre-built lookup structures
  private computeDynamicHierarchy(queryPrefix: string) {
    try {
      const [queryIP, queryMaskStr] = queryPrefix.split('/');
      const queryMask = parseInt(queryMaskStr);
      
      if (!queryIP || isNaN(queryMask)) return null;

      let hasROAChildren = false;
      let roaChildrenCount = 0;
      let parentHasROA = false;

      // Optimized: Only check longer masks for children
      const maskLengths = Array.from(this.prefixesByMask.keys()).sort((a, b) => a - b);
      
      for (const mask of maskLengths) {
        if (mask <= queryMask) continue; // Skip same or shorter masks for children
        
        const prefixes = this.prefixesByMask.get(mask) || [];
        for (const roaPrefix of prefixes) {
          const roaInfo = this.roaData[roaPrefix];
          if (roaInfo?.hasROA && this.prefixContains(queryPrefix, roaPrefix)) {
            hasROAChildren = true;
            roaChildrenCount++;
            // Limit count for performance
            if (roaChildrenCount >= 100) break;
          }
        }
        if (roaChildrenCount >= 100) break;
      }

      // Optimized: Only check shorter masks for parents
      for (const mask of maskLengths) {
        if (mask >= queryMask) continue; // Skip same or longer masks for parents
        
        const prefixes = this.prefixesByMask.get(mask) || [];
        for (const roaPrefix of prefixes) {
          const roaInfo = this.roaData[roaPrefix];
          if (roaInfo?.hasROA && this.prefixContains(roaPrefix, queryPrefix)) {
            parentHasROA = true;
            break;
          }
        }
        if (parentHasROA) break;
      }

      // Return computed hierarchy info
      return {
        asn: 'Unknown',
        maxLength: queryMask,
        hasROA: false,
        ta: 'unknown',
        hasROAChildren,
        roaChildrenCount,
        parentHasROA
      };
    } catch {
      return null;
    }
  }

  // Find ROAs that cover a larger prefix (e.g., find /24s within a /16)
  getROAsInPrefix(topPrefix: string): ProcessedROAData {
    const result: ProcessedROAData = {};
    
    // Simple string matching for demo - in production you'd want proper IP subnet matching
    for (const [prefix, roaInfo] of Object.entries(this.roaData)) {
      if (this.prefixContains(topPrefix, prefix)) {
        result[prefix] = roaInfo;
      }
    }
    
    return result;
  }

  // Simple prefix containment check (for demo purposes)
  private prefixContains(containerPrefix: string, testPrefix: string): boolean {
    try {
      const [containerIP, containerMask] = containerPrefix.split('/');
      const [testIP, testMask] = testPrefix.split('/');
      
      const containerMaskNum = parseInt(containerMask);
      const testMaskNum = parseInt(testMask);
      
      // Test prefix must have longer or equal mask
      if (testMaskNum < containerMaskNum) {
        return false;
      }
      
      // Simple IP prefix matching for IPv4
      if (containerIP.includes('.') && testIP.includes('.')) {
        return testIP.startsWith(containerIP.split('.').slice(0, Math.ceil(containerMaskNum / 8)).join('.'));
      }
      
      return false;
    } catch {
      return false;
    }
  }

  // Get statistics about loaded ROA data
  getStats() {
    const total = Object.keys(this.roaData).length;
    const ipv4Count = Object.keys(this.roaData).filter(p => p.includes('.')).length;
    const ipv6Count = total - ipv4Count;
    
    return {
      total,
      ipv4: ipv4Count,
      ipv6: ipv6Count,
      lastUpdate: new Date(this.lastFetch).toISOString()
    };
  }
}

// Singleton instance
export const rpkiService = new RPKIService();

// Export types and utilities
export { RPKIService };